'use client';

import { type ReactNode, useMemo, useRef, useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { CobrowseProvider } from '@/providers/cobrowse-provider';
import { PresentationModeProvider } from '@/providers/presentation-mode-provider';
import { RepSessionProvider, useRepSessionContext, hasLocalConsent } from '@/providers/rep-session-provider';
import { SyncBusProvider } from '@/providers/sync-bus-provider';
import { useClientRecovery } from '@/hooks/use-client-recovery';

const CobrowseConsentModal = dynamic(
  () => import('@/components/cobrowse/consent-modal').then((m) => m.CobrowseConsentModal),
  { ssr: false },
);

const SESSION_STORAGE_KEY_PREFIX = 'red-rep-session:';

function usePersistedSessionToken(opportunityId: string): string | null {
  const storageKey = `${SESSION_STORAGE_KEY_PREFIX}${opportunityId}`;
  const capturedRef = useRef(false);

  const [token] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('session');
    if (fromUrl) return fromUrl;
    try {
      return sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (capturedRef.current || !token) return;
    capturedRef.current = true;

    try {
      sessionStorage.setItem(storageKey, token);
    } catch { /* private browsing */ }

    const url = new URL(window.location.href);
    if (url.searchParams.has('session')) {
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
    }
  }, [token, storageKey]);

  return token;
}

function ConsentModalMount() {
  const rep = useRepSessionContext();
  return (
    <CobrowseConsentModal
      isOpen={rep.showConsentModal}
      repName={rep.sessionData?.repName}
      error={rep.sessionError}
      onAccept={rep.handleConsentAccept}
      onDecline={rep.handleConsentDecline}
    />
  );
}

function ClientRecoveryMount({ opportunityId }: { opportunityId: string }) {
  useClientRecovery({ opportunityId });
  return null;
}

export function OpportunityProviders({ children }: { children: ReactNode }) {
  const params = useParams();
  const opportunityId = params.opportunityId as string;

  const sessionToken = usePersistedSessionToken(opportunityId);

  const cobrowseEnabled = useMemo(() => {
    if (sessionToken) return true;
    if (typeof window === 'undefined') return false;
    return hasLocalConsent(opportunityId);
  }, [opportunityId, sessionToken]);

  return (
    <SyncBusProvider opportunityId={opportunityId} role="client">
      <ClientRecoveryMount opportunityId={opportunityId} />
      <PresentationModeProvider>
        <CobrowseProvider opportunityId={opportunityId} enabled={cobrowseEnabled}>
          <RepSessionProvider opportunityId={opportunityId} sessionToken={sessionToken}>
            {children}
            <ConsentModalMount />
          </RepSessionProvider>
        </CobrowseProvider>
      </PresentationModeProvider>
    </SyncBusProvider>
  );
}
